import { FilesCollection, makeKey } from '@/stores/useFiles.ts'
import React, { Context, createContext, useContext, useEffect, useMemo } from 'react'
import { Album } from '@/models/album.ts'
import { useAlbums } from '@/stores/useAlbums.ts'
import { InfiniteQueryObserverResult, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import { useApi } from '@/stores/useApi.ts'
import { useUploadQueue } from '@/stores/useUploadQueue.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { getDemoFilesForDrive } from '@/controller/demo.ts'
import { useParams } from 'react-router-dom'
import { useFileFilter } from '@/contexts/fileFilter.tsx'
import { useProfile } from '@/stores/useAuth.ts'
import { useAllFilesContext } from '@/contexts/allFilesContext.tsx'

export interface FilesContext {
  currentFilesCollection: FilesCollection | null
  currentAlbum: Album | null
  currentAlbumId: string | null
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage: () => Promise<InfiniteQueryObserverResult>
  isLoading: boolean
  /** Total number of files on the server (DRF `count`), independent of how many are loaded. */
  totalCount?: number
  /** Number of server files loaded so far (across fetched pages). */
  loadedCount?: number
  queryKey: Array<string | null>
  updateFile: (updatedAlbumFile: AlbumFile) => void
}

export const FilesContext = createContext<FilesContext | null>(null)

export function FilesProvider({ children, filesFilter, albumId: propAlbumId, FilesContext: PropFilesContext, uploadedFilesFilter }: { children: React.ReactNode, albumId?: string, filesFilter?: Record<string, string>, FilesContext?: Context<FilesContext | null>, uploadedFilesFilter?: (file: AlbumFile) => boolean  }) {
  const { albums } = useAlbums()
  const { drives, currentDrive } = useDrives()
  const api = useApi()
  const { getFiles } = useClient()
  const { albumId: albumIdFromParams } = useParams<{albumId?: string}>()
  const { uploadedFiles, deletedFiles, updateUploadedFile } = useUploadQueue()
  const albumId = propAlbumId || albumIdFromParams || (currentDrive && drives[currentDrive.id]?.albums?.find(a => a.is_primary)?.id || null)
  const { fileFilter } = useFileFilter()
  const queryClient = useQueryClient()
  const { isGuestMode } = useProfile()
  const allFilesContext = useAllFilesContext()
  // const [uploadedFiles, setUploadedFiles] = useState<Array<AlbumFile>>([])

  // const { album, appendFiles } = useFiles()
  const currentAlbum = albumId ? (albums[albumId] || null ) : null

  const queryKey = currentAlbum ? makeKey(currentAlbum.id, filesFilter || fileFilter) : [null]
  // @ts-ignore
  const query = useInfiniteQuery({
    enabled: currentAlbum != null,
    queryKey,
    async queryFn({ pageParam }) {
      if (pageParam) {
        // @ts-ignore
        return (await api.get(pageParam)).data
      }

      let finalFileFilter = filesFilter || fileFilter
      if (!finalFileFilter && currentAlbum && currentAlbum.default_files_order) {
        finalFileFilter = { sort: currentAlbum.default_files_order }
      }

      if (!finalFileFilter && currentDrive && currentDrive.default_files_order) {
        finalFileFilter = { sort: currentDrive.default_files_order }
      }

      return (await getFiles(currentAlbum!, finalFileFilter)).data
    },
    getNextPageParam(page) {
      // @ts-ignore
      return page.next
    },
  })

  const fetchedFiles = useMemo(() => {
    if (!query.data) {
      return []
    }

    const data = query.data
    const files: Array<AlbumFile> = [
      // @ts-ignore
      ...(data ? data.pages.flatMap(page => page.results) : [])
    ]

    let filteredUploadedFiles: Array<AlbumFile> = []
    if (currentAlbum) {
      filteredUploadedFiles = uploadedFiles[currentAlbum.id] || []
      if (uploadedFilesFilter) {
        filteredUploadedFiles = filteredUploadedFiles.filter(uploadedFilesFilter)
      }

      filteredUploadedFiles = filteredUploadedFiles.filter(file => !deletedFiles.has(file.id) && !files.find(f => f.id === file.id))
    }

    return [
      ...filteredUploadedFiles,
      ...files.filter(file => !deletedFiles.has(file.id)),
      ...(
        currentDrive?.is_demo ? getDemoFilesForDrive(currentDrive, isGuestMode, currentAlbum || undefined) : []
      ),
    ]
  }, [currentAlbum, deletedFiles, query.data, uploadedFiles, currentDrive?.is_demo]);

  // Total (server) count and number of loaded server files, for "load more" / "load all" controls
  // @ts-ignore
  const totalCount: number | undefined = query.data?.pages?.[0]?.count
  const loadedCount = useMemo(() => {
    if (!query.data) {
      return 0
    }
    // @ts-ignore
    return query.data.pages.reduce((acc: number, page: any) => acc + (page.results?.length ?? 0), 0)
  }, [query.data])

  // Registriere Dateien beim AllFilesContext (wenn vorhanden)
  // Dateien werden NICHT beim Unmount deregistriert, damit alle besuchten Alben erhalten bleiben
  // Nur die Datei-IDs als Dependency verwenden, um Infinite Loops zu vermeiden
  const fetchedFileIds = useMemo(() => fetchedFiles.map(f => f.id).join(','), [fetchedFiles])
  useEffect(() => {
    if (allFilesContext && albumId && fetchedFiles.length > 0) {
      allFilesContext.registerAlbumFiles(albumId, fetchedFiles)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumId, fetchedFileIds])

  function updateFile(updatedAlbumFile: AlbumFile) {
    // @ts-ignore
    queryClient.setQueryData(queryKey, (prev: any) => {
      if (!prev) {
        return undefined
      }

      console.log('prev', prev)
      return {
        ...prev,
        pages: prev.pages.map((page: any) => ({
          ...page,
          results: page.results.map((file: AlbumFile) => file.id === updatedAlbumFile.id ? updatedAlbumFile : file)
        }))
      }
    })
    updateUploadedFile(updatedAlbumFile)
  }

  // function addUploadedFiles(album: FridaySnap, files: Array<AlbumFile>) {
  //   const currentlyUploaded = uploadedFiles[album.id] || []
  //
  //   setUploadedFiles({
  //     ...uploadedFiles,
  //     [album.id]: [...currentlyUploaded, ...files],
  //   })
  // }
  //
  // function addDeletedFiles(files: Array<AlbumFile>) {
  //   const newFiles = new Set(deletedFiles)
  //
  //   for (const file of files) {
  //     newFiles.add(file.id)
  //   }
  //
  //   setDeletedFiles(newFiles)
  //   // setUploadedFiles(uploadedFiles.filter(uploadedFile => newFiles.has(uploadedFile.id)))
  // }

  const MyContext = PropFilesContext || FilesContext

  return <MyContext.Provider value={{
    currentFilesCollection: {
      files: fetchedFiles,
    },
    currentAlbum,
    currentAlbumId: albumId,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    isLoading: query.isLoading,
    totalCount,
    loadedCount,
    queryKey,
    updateFile,
    // uploadedFiles,
    // addUploadedFiles,
    // deletedFiles,
    // addDeletedFiles,
  }}>
    { children }
  </MyContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFilesContext() {
  return useContext(FilesContext)
}
